import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Save, Send, Trash2, X, Loader2 } from 'lucide-react';
import { TaskList } from './TaskList';
import { sendJournalDataToWebhook } from '../lib/webhook';
import { saveJournalEntry, fetchJournalEntry } from '../lib/supabase';
import { DifficultySelector } from './DifficultySelector';
import { DIFFICULTY_TASKS } from '../lib/store';

interface Task {
  id: string;
  text: string;
  completed: boolean;
}

interface JournalEntryProps {
  date: Date;
  entry?: any;
  onSave: () => void;
  onDelete?: () => void;
  onPremiumContent: () => boolean;
}

export function JournalEntry({ date, entry, onSave, onDelete, onPremiumContent }: JournalEntryProps) {
  const [formData, setFormData] = useState({
    id: undefined as string | undefined,
    date: format(date, 'yyyy-MM-dd'),
    day_description: '',
    day_rating: 5,
    energy_givers: '',
    energy_takers: '',
    triggers: '',
    tasks: [] as Task[],
    motivation: '',
    ideal_life: '',
    difficulty_level: 'easy'
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showMotivation, setShowMotivation] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadEntry();
  }, [date]);

  useEffect(() => {
    if (entry) {
      const existingTasks = Array.isArray(entry.tasks) ? entry.tasks : [];
      const defaultTasks = DIFFICULTY_TASKS[entry.difficulty_level as keyof typeof DIFFICULTY_TASKS] || [];
      
      // Filter out any existing tasks that match default task names
      const customTasks = existingTasks.filter(task => 
        !defaultTasks.some(defaultTask => defaultTask.text === task.text)
      );

      setFormData({
        id: entry.id,
        date: format(date, 'yyyy-MM-dd'),
        day_description: entry.day_description || '',
        day_rating: entry.day_rating || 5,
        energy_givers: entry.energy_givers || '',
        energy_takers: entry.energy_takers || '',
        triggers: entry.triggers || '',
        tasks: [...defaultTasks, ...customTasks],
        motivation: entry.motivation || '',
        ideal_life: entry.ideal_life || '',
        difficulty_level: entry.difficulty_level || 'easy'
      });
      setHasUnsavedChanges(false);
      setIsLoading(false);
    } else {
      setFormData({
        id: undefined,
        date: format(date, 'yyyy-MM-dd'),
        day_description: '',
        day_rating: 5,
        energy_givers: '',
        energy_takers: '',
        triggers: '',
        tasks: DIFFICULTY_TASKS.easy,
        motivation: '',
        ideal_life: '',
        difficulty_level: 'easy'
      });
      setHasUnsavedChanges(true);
      setIsLoading(false);
    }
  }, [entry, date]);

  const loadEntry = async () => {
    if (entry) return;

    try {
      setIsLoading(true);
      const formattedDate = format(date, 'yyyy-MM-dd');
      const { data: existingEntry } = await fetchJournalEntry(formattedDate);
      
      if (existingEntry) {
        const existingTasks = Array.isArray(existingEntry.tasks) ? existingEntry.tasks : [];
        const defaultTasks = DIFFICULTY_TASKS[existingEntry.difficulty_level as keyof typeof DIFFICULTY_TASKS] || [];
        
        // Filter out any existing tasks that match default task names
        const customTasks = existingTasks.filter(task => 
          !defaultTasks.some(defaultTask => defaultTask.text === task.text)
        );

        setFormData({
          id: existingEntry.id,
          date: formattedDate,
          day_description: existingEntry.day_description || '',
          day_rating: existingEntry.day_rating || 5,
          energy_givers: existingEntry.energy_givers || '',
          energy_takers: existingEntry.energy_takers || '',
          triggers: existingEntry.triggers || '',
          tasks: [...defaultTasks, ...customTasks],
          motivation: existingEntry.motivation || '',
          ideal_life: existingEntry.ideal_life || '',
          difficulty_level: existingEntry.difficulty_level || 'easy'
        });
      } else {
        setFormData({
          id: undefined,
          date: formattedDate,
          day_description: '',
          day_rating: 5,
          energy_givers: '',
          energy_takers: '',
          triggers: '',
          tasks: DIFFICULTY_TASKS.easy,
          motivation: '',
          ideal_life: '',
          difficulty_level: 'easy'
        });
      }
      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error('Error loading entry:', error);
      showNotification(error.message || 'Failed to load entry', true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDifficultyChange = (difficulty: string) => {
    const defaultTasks = DIFFICULTY_TASKS[difficulty as keyof typeof DIFFICULTY_TASKS];
    const existingTasks = formData.tasks;
    
    // Keep custom tasks (tasks not in any difficulty level)
    const customTasks = existingTasks.filter(task => 
      !Object.values(DIFFICULTY_TASKS).flat().some(defaultTask => 
        defaultTask.text === task.text
      )
    );

    setFormData(prev => ({
      ...prev,
      difficulty_level: difficulty,
      tasks: [...defaultTasks, ...customTasks]
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!hasUnsavedChanges) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const { data, error } = await saveJournalEntry({
        ...formData,
        date: format(date, 'yyyy-MM-dd')
      });

      if (error) throw error;

      if (data) {
        setFormData(prev => ({
          ...prev,
          id: data.id
        }));
        setHasUnsavedChanges(false);
        showNotification('Journal entry saved successfully');
        onSave();
      }
    } catch (error: any) {
      console.error('Error saving journal entry:', error);
      showNotification(error.message || 'Failed to save journal entry', true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSend = async () => {
    if (hasUnsavedChanges) {
      await handleSave();
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await sendJournalDataToWebhook(formData);

      if (response?.currentDay?.motivation) {
        const updatedData = {
          ...formData,
          motivation: response.currentDay.motivation
        };

        const { data, error } = await saveJournalEntry(updatedData);

        if (error) throw error;

        if (data) {
          setFormData(prev => ({
            ...prev,
            motivation: data.motivation
          }));
          setHasUnsavedChanges(false);
          setShowMotivation(true);
        }
      }

      showNotification('Journal entry processed successfully');
      onSave();
    } catch (error: any) {
      console.error('Error processing journal entry:', error);
      showNotification(error.message || 'Failed to process journal entry', true);
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!formData.id || !onDelete) return;

    setIsDeleting(true);
    setError(null);

    try {
      const { error } = await saveJournalEntry({
        id: formData.id,
        _delete: true
      });

      if (error) throw error;

      onDelete();
      showNotification('Journal entry deleted successfully');
    } catch (error: any) {
      console.error('Error deleting journal entry:', error);
      showNotification(error.message || 'Failed to delete journal entry', true);
    } finally {
      setIsDeleting(false);
    }
  };

  const showNotification = (message: string, isError = false) => {
    if (isError) {
      setError(message);
      setSuccessMessage(null);
    } else {
      setSuccessMessage(message);
      setError(null);
    }
    setTimeout(() => {
      setError(null);
      setSuccessMessage(null);
    }, 5000);
  };

  if (isLoading) {
    return (
      <div className="bg-white p-8 rounded-[30px] shadow-xl">
        <div className="flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-custom-orange-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {(error || successMessage) && (
        <div className={`fixed top-4 right-4 p-4 rounded-xl shadow-lg z-50 ${
          error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
        }`}>
          {error || successMessage}
        </div>
      )}

      {showMotivation && formData.motivation && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-white p-6 rounded-xl shadow-lg z-50 max-w-md w-full animate-fade-in">
          <button
            onClick={() => setShowMotivation(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
          <h3 className="text-lg font-semibold text-custom-orange-600 mb-2">Today's Motivation</h3>
          <p className="text-custom-orange-700">{formData.motivation}</p>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6 bg-white p-8 rounded-[30px] shadow-xl">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-700">
            {format(date, 'MMMM d, yyyy')}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!hasUnsavedChanges || isSaving}
              className={`px-6 py-2 bg-gradient-to-r from-custom-orange-300 to-custom-orange-400 text-white rounded-2xl hover:from-custom-orange-400 hover:to-custom-orange-500 transition-all shadow-lg flex items-center gap-2 ${
                (!hasUnsavedChanges || isSaving) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Save size={20} className={isSaving ? 'animate-pulse' : ''} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending}
              className={`px-6 py-2 bg-gradient-to-r from-blue-400 to-blue-500 text-white rounded-2xl hover:from-blue-500 hover:to-blue-600 transition-all shadow-lg flex items-center gap-2 ${
                isSending ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Send size={20} className={isSending ? 'animate-pulse' : ''} />
              {isSending ? 'Sending...' : 'Send'}
            </button>
            {formData.id && onDelete && (
              <button
                type="button"
                onClick={handleDeleteEntry}
                disabled={isDeleting}
                className={`px-6 py-2 bg-gradient-to-r from-red-300 to-red-400 text-white rounded-2xl hover:from-red-400 hover:to-red-500 transition-all shadow-lg flex items-center gap-2 ${
                  isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Trash2 size={20} className={isDeleting ? 'animate-pulse' : ''} />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <DifficultySelector
            value={formData.difficulty_level}
            onChange={handleDifficultyChange}
          />

          <TaskList
            tasks={formData.tasks}
            onTaskAdd={() => {
              const newTask = { id: crypto.randomUUID(), text: '', completed: false };
              setFormData(prev => ({
                ...prev,
                tasks: [...prev.tasks, newTask]
              }));
              setHasUnsavedChanges(true);
            }}
            onTaskToggle={(taskId) => {
              setFormData(prev => ({
                ...prev,
                tasks: prev.tasks.map(task =>
                  task.id === taskId ? { ...task, completed: !task.completed } : task
                )
              }));
              setHasUnsavedChanges(true);
            }}
            onTaskDelete={(taskId) => {
              const taskToDelete = formData.tasks.find(t => t.id === taskId);
              // Only allow deletion of custom tasks
              if (taskToDelete && !Object.values(DIFFICULTY_TASKS).flat().some(dt => dt.text === taskToDelete.text)) {
                setFormData(prev => ({
                  ...prev,
                  tasks: prev.tasks.filter(task => task.id !== taskId)
                }));
                setHasUnsavedChanges(true);
              }
            }}
            onTaskTextChange={(taskId, text) => {
              setFormData(prev => ({
                ...prev,
                tasks: prev.tasks.map(task =>
                  task.id === taskId ? { ...task, text } : task
                )
              }));
              setHasUnsavedChanges(true);
            }}
          />

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Tell me about your day
            </label>
            <textarea
              value={formData.day_description}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, day_description: e.target.value }));
                setHasUnsavedChanges(true);
              }}
              className="w-full h-32 px-4 py-3 bg-custom-orange-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200 text-gray-700 placeholder-gray-400"
              placeholder="Write about your day..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              How was your day? ({formData.day_rating}/10)
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={formData.day_rating}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, day_rating: parseInt(e.target.value) }));
                setHasUnsavedChanges(true);
              }}
              className="w-full h-2 bg-custom-orange-100 rounded-lg appearance-none cursor-pointer accent-custom-orange-300"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              What/who gave you energy?
            </label>
            <input
              type="text"
              value={formData.energy_givers}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, energy_givers: e.target.value }));
                setHasUnsavedChanges(true);
              }}
              className="w-full px-4 py-3 bg-custom-orange-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200 text-gray-700 placeholder-gray-400"
              placeholder="People or activities that energized you..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Who took your energy?
            </label>
            <input
              type="text"
              value={formData.energy_takers}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, energy_takers: e.target.value }));
                setHasUnsavedChanges(true);
              }}
              className="w-full px-4 py-3 bg-custom-orange-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200 text-gray-700 placeholder-gray-400"
              placeholder="People or situations that drained you..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Tell me about your triggers
            </label>
            <textarea
              value={formData.triggers}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, triggers: e.target.value }));
                setHasUnsavedChanges(true);
              }}
              className="w-full h-32 px-4 py-3 bg-custom-orange-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200 text-gray-700 placeholder-gray-400"
              placeholder="What triggered you today?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Describe your ideal life
            </label>
            <textarea
              value={formData.ideal_life}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, ideal_life: e.target.value }));
                setHasUnsavedChanges(true);
              }}
              className="w-full h-32 px-4 py-3 bg-custom-orange-50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200 text-gray-700 placeholder-gray-400"
              placeholder="What does your ideal life look like?"
            />
          </div>

          {formData.motivation && (
            <div className="bg-gradient-to-r from-custom-orange-50 to-custom-orange-100 p-4 rounded-2xl">
              <h3 className="font-semibold text-custom-orange-600 mb-2">Today's Motivation</h3>
              <p className="text-custom-orange-700">{formData.motivation}</p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}